import { useState } from 'react'
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { skipToken } from '@reduxjs/toolkit/query'

import { blueGrey } from '@mui/material/colors'
import { Box, Container, Divider, Paper, Stack, Typography, Button } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import InviteButtonMenu from './components/InviteButtonMenu'

import HeartbeatLoader from '@/components/ui/loaders/HeartbeatLoader'
import PageContainer from '@/components/ui/containers/PageContainer'

import { useGetMyNetworkInvitationsQuery } from '@/core/services/auth'
import { useDeleteNetworkMutation, useGetNetworkQuery, useReplyNetworkInvitationMutation } from '@/core/services/network'

import NotFoundPage from '@/pages/error/NotFoundPage'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { getApiErrorMessage } from '@/utils/errors'

import CancelJoinRequestButton from './components/CancelJoinRequestButton'
import ConsultationOrders from './containers/ConsultationOrders'
import ExpertiseOrders from './containers/ExpertiseOrders'
import JoinNetworkButton from './components/JoinNetworkButton'
import LeaveNetworkButton from './components/LeaveNetworkButton'
import { MobileNetworkDrawer } from './components/MobileNetworkDrawer'
import { MAIN_HEADER_HEIGHT } from '@/layouts/main'
import MembersContainer from './containers/MobileMembers'
import { NetworkResume } from './components/NetworkResume'
import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'
import MemberRequests from './containers/MemberRequests'
import NetworkSettingsContainer from './containers/NetworkSettings'
import NetworkWaitingRooms from './containers/NetworkWaitingRooms'
import NetworkCreateWaitingRoom from './containers/NetworkCreateWaitingRoom'
import WaitingRoomConsultations from './containers/WaitingRoomConsultations'
import MemberSchedulesTable from './containers/MemberSchedulesTable'
import NetworkPlannings from './containers/NetworkPlannings'
import { useNetworkMemberPermissions } from './hooks/useNetworkMemberPermissions'
import { NetworkDailyOverview } from './components/NetworkDailyOverview'

export default function MobileNetworkPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { networkId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)

  const { data: network, error: getNetworkError, isLoading: isFetchingNetwork } = useGetNetworkQuery(networkId ?? skipToken)
  const { data: networkInvitations } = useGetMyNetworkInvitationsQuery({ skip: 0 }, { refetchOnMountOrArgChange: true })
  const [replyNetworkInvitation, { isLoading: isReplyingNetworkInvitation, originalArgs: replyNetworkInvitationArgs }] = useReplyNetworkInvitationMutation()
  const [deleteNetwork, { isLoading: isDeletingNetwork }] = useDeleteNetworkMutation()

  const { isNetworkAdmin } = useNetworkMemberPermissions({ networkId })

  const [openDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] = useState(false)

  const isNetworkMember = !!network?.members.find((member) => member === authUser?._id)
  const networkInvitation = networkInvitations?.find((networkInvitation) => networkInvitation.network._id === networkId)

  if (!authUser) return <Navigate to="/login" />

  if (isFetchingNetwork) {
    return (
      <Stack flex={1} alignItems="center" justifyContent="center">
        <HeartbeatLoader sx={{ transform: 'scale(4)' }} />
      </Stack>
    )
  }

  if (!!getNetworkError || !network) return <NotFoundPage />

  const isRequestingToJoinNetwork = !!network.pendingMembers.find((pendingMember) => pendingMember._id === authUser?._id)
  const isPlanningRoute = location.pathname.includes('/plannings')

  return (
    <Routes>
      <Route
        path="/"
        element={
          <Stack flex={1} sx={{ maxHeight: `calc(100vh - ${MAIN_HEADER_HEIGHT}px)` }}>
            {!isPlanningRoute && <NetworkResume network={network} oversize={true} />}
            <MobileNetworkDrawer network={network} />
            <Box flex={1} sx={{ overflow: 'auto' }}>
              <Outlet />
            </Box>
          </Stack>
        }
      >
        <Route path="member-requests" element={<MemberRequests />} />
        <Route path="edit" element={<NetworkSettingsContainer />} />
        <Route path="waiting-rooms" element={<NetworkWaitingRooms />} />
        <Route path="waiting-rooms/create" element={<NetworkCreateWaitingRoom />} />
        <Route path="waiting-rooms/:waitingRoomId/consultations" element={<WaitingRoomConsultations />} />
        <Route path="members" element={<MembersContainer />} />
        <Route path="schedules" element={<MemberSchedulesTable />} />
        <Route path="plannings/*" element={<Box sx={{ paddingBottom: 8 }}><NetworkPlannings /></Box>} />
        <Route
          path="consultation"
          element={
            <Box sx={{ paddingBottom: 8 }}>
              <Stack pt={2} px={2} spacing={2}>
                <Typography component="h2" variant="h2">
                  {t('page.network.tab.consultationOrders.label')}
                </Typography>
                <ConsultationOrders />
              </Stack>
            </Box>
          }
        />
        <Route
          path="expertise"
          element={
            <Box sx={{ paddingBottom: 8 }}>
              <Stack pt={2} px={2} spacing={2}>
                <Typography component="h2" variant="h2">
                  {t('page.network.tab.expertiseOrders.label')}
                </Typography>
                <ExpertiseOrders />
              </Stack>
            </Box>
          }
        />
        <Route
          index
          element={
            <Box sx={{ paddingBottom: 8 }}>
              <Container maxWidth="xl" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Stack spacing={1} sx={{ pl: '10px', pr: '10px' }}>
                  <Stack>
                    <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between" py={1}>
                      {isNetworkMember && (
                        <>
                          <InviteButtonMenu networkId={network._id} />
                          <LeaveNetworkButton networkId={network._id} />
                          {isNetworkAdmin && (
                            <Button variant="contained" color="error" onClick={() => setOpenDeleteConfirmationDialog(true)}>
                              <DeleteOutlineIcon /> {t('common:button.delete.label')}
                            </Button>
                          )}
                        </>
                      )}
                      {!isNetworkMember && !networkInvitation ? (
                        !isRequestingToJoinNetwork ? (
                          <JoinNetworkButton network={network} />
                        ) : (
                          <CancelJoinRequestButton applicant={authUser._id} networkId={network._id} />
                        )
                      ) : null}
                    </Stack>
                    {!!networkInvitation && !isNetworkMember && (
                      <Stack
                        flex={1}
                        bgcolor={blueGrey[100]}
                        borderRadius={2}
                        direction={{ xs: 'column', lg: 'row' }}
                        alignItems={{ xs: 'start', lg: 'center' }}
                        justifyContent="space-between"
                        p={2}
                        spacing={2}
                      >
                        <Typography component="span" sx={{ fontWeight: 700 }}>
                          {t('page.networks.invitationNetwork.invitedBy', {
                            inviter: `${networkInvitation.inviter.userInfo.name} ${networkInvitation.inviter.userInfo.surname}`,
                          })}
                        </Typography>
                        <Stack direction="row" spacing={2}>
                          <LoadingButton
                            loading={isReplyingNetworkInvitation && replyNetworkInvitationArgs?.action === 'accept'}
                            onClick={async () => {
                              try {
                                await replyNetworkInvitation({ networkId: networkInvitation.network._id, action: 'accept' }).unwrap()
                                navigate(`/networks/${networkInvitation.network._id}`)
                              } catch (error) {
                                enqueueSnackbar(getApiErrorMessage(error, t('notification.network.acceptInvitation.error')), { variant: 'error' })
                              }
                            }}
                            size="small"
                            variant="contained"
                          >
                            {t('common:button.join.label')}
                          </LoadingButton>
                          <LoadingButton
                            loading={isReplyingNetworkInvitation && replyNetworkInvitationArgs?.action === 'decline'}
                            onClick={async () => {
                              try {
                                await replyNetworkInvitation({ networkId: networkInvitation.network._id, action: 'decline' }).unwrap()
                              } catch (error) {
                                enqueueSnackbar(getApiErrorMessage(error, t('notification.network.declineInvitation.error')), { variant: 'error' })
                              }
                            }}
                            size="small"
                            variant="outlined"
                          >
                            {t('common:button.decline.label')}
                          </LoadingButton>
                        </Stack>
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              </Container>
              {isNetworkMember ? (
                <Box sx={{ px: 2, py: 2 }}>
                  <NetworkDailyOverview isNetworkAdmin={isNetworkAdmin} isNetworkMember={isNetworkMember} network={network} />
                </Box>
              ) : (
                <PageContainer sx={{ minHeight: '300px', mt: 2 }}>
                  <Paper>
                    <Box sx={{ px: { xs: 2, sm: 3 }, py: '20px' }}>
                      <Typography component="h2" sx={{ color: (theme) => theme.palette.grey[900], fontSize: '18px', lineHeight: '24px' }}>
                        {t('page.network.about.title')}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ px: { xs: 2, sm: 3 }, py: '20px' }}>
                      <Typography component="p" sx={{ color: (theme) => theme.palette.grey[900], fontSize: '14px' }}>
                        {network.description}
                      </Typography>
                    </Box>
                  </Paper>
                </PageContainer>
              )}
              <DeleteConfirmationDialog
                isDeleting={isDeletingNetwork}
                open={openDeleteConfirmationDialog}
                onClose={() => {
                  setOpenDeleteConfirmationDialog(false)
                }}
                onDelete={async () => {
                  try {
                    await deleteNetwork(network._id).unwrap()
                    setOpenDeleteConfirmationDialog(false)
                    navigate('/networks')
                  } catch (error) {
                    enqueueSnackbar(getApiErrorMessage(error, t('notification.apiKey.delete.error')), { variant: 'error' })
                  }
                }}
                title={t('dialog.deleteConfirmation.network.title')}
                message={t('dialog.deleteConfirmation.network.message')}
              />
            </Box>
          }
        />
      </Route>
    </Routes>
  )
}
